import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Card, Row, Col, Tooltip, Alert, Divider } from 'antd';
import ReactECharts from 'echarts-for-react';

// 样式定义
const styles = {
  analysisItem: {
    textAlign: 'center' as const,
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '4px',
  },
  analysisTitle: {
    color: '#666',
    marginBottom: '8px',
  },
  analysisValue: {
    fontSize: '20px',
    fontWeight: 'bold' as const,
    color: '#1890ff',
  },
};

// 定义柔性度数据点类型
interface FlexibilityDataPoint {
  position: number;
  flexibility: number;
  aminoAcid?: string;
}

// 柔性度结果展示组件
const FlexibilityResult: React.FC<ResultComponentProps> = ({
  data,
  sequence,
  title,
  onDeleteResult,
  multiResults,
}) => {
  const normalizedResults = React.useMemo(() => {
    if (data && data.results && Array.isArray(data.results)) {
      if (data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)) {
        return data.results[0].result.results;
      }
      return data.results;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [] as any[];
  }, [data]);

  // 支持多序列显示
  if (normalizedResults.length > 1) {
    const successResults = normalizedResults.filter((item: any) => (item.success !== false) && (item.result?.flexibility_profile || item.flexibility_profile));

    if (successResults.length === 0) {
      return (
        <BaseResultDisplay
          title={title || '蛋白质柔性度分析'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Alert
            message="无有效数据"
            description="没有找到有效的柔性度分析结果。"
            type="warning"
            showIcon
          />
        </BaseResultDisplay>
      );
    }

    return (
      <BaseResultDisplay
        title={title || `蛋白质柔性度分析（${successResults.length}个序列）`}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        {successResults.map((item: any, index: number) => {
          const result = item.result || item;
          const flexData: FlexibilityDataPoint[] = [];

          if (Array.isArray(result.flexibility_profile)) {
            result.flexibility_profile.forEach((value: any, idx: number) => {
              flexData.push({
                position: idx + 1,
                flexibility: Number(value),
              });
            });
          }

          if (flexData.length === 0) return null;

          flexData.sort((a, b) => a.position - b.position);
          const chartData = flexData.map(point => [point.position, point.flexibility]);
          const currentSequence = item.sequence || result.sequence || '';

          const option = {
            tooltip: {
              trigger: 'axis',
              formatter: (params: any) => {
                const dataPoint = params[0];
                const idx = dataPoint.dataIndex;
                const aa = currentSequence && currentSequence.length > idx ? currentSequence.charAt(idx) : '';
                return `位置: ${dataPoint.data[0]}<br/>柔性度: ${dataPoint.data[1].toFixed(2)}<br/>氨基酸: ${aa}`;
              },
            },
            grid: {
              left: '5%',
              right: '5%',
              bottom: '10%',
              top: '10%',
              containLabel: true,
            },
            xAxis: {
              type: 'value',
              name: '残基位置',
              nameLocation: 'middle',
              nameGap: 30,
              min: Math.min(...flexData.map(point => point.position)),
              max: Math.max(...flexData.map(point => point.position)),
            },
            yAxis: {
              type: 'value',
              name: '柔性度值',
              nameLocation: 'middle',
              nameGap: 40,
            },
            series: [
              {
                type: 'line',
                data: chartData,
                smooth: true,
                lineStyle: {
                  width: 2,
                  color: '#722ed1',
                },
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: {
                  color: '#722ed1',
                },
              },
            ],
          };

          return (
            <div key={index}>
              {index > 0 && <Divider />}
              <Card 
                title={`序列 ${item.sequence_id || item.id || (index + 1)} - 柔性度分析`}
                style={{ marginBottom: 16 }}
              >
                <ReactECharts
                  option={option}
                  style={{ height: '400px', width: '100%' }}
                  notMerge={true}
                />
                <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                  序列长度: {flexData.length} | 平均柔性度: {(flexData.reduce((sum, p) => sum + p.flexibility, 0) / flexData.length).toFixed(2)}
                </div>
              </Card>
            </div>
          );
        })}
      </BaseResultDisplay>
    );
  }

  // 单序列结果直接渲染（复用多序列的渲染逻辑，避免递归调用自身导致无限循环）
  const singleEntry = normalizedResults.length === 1 ? normalizedResults[0] : null;

  if (singleEntry && (singleEntry.result?.flexibility_profile || singleEntry.flexibility_profile)) {
    const result = singleEntry.result || singleEntry;
    const flexData: FlexibilityDataPoint[] = [];

    if (Array.isArray(result.flexibility_profile)) {
      result.flexibility_profile.forEach((value: any, idx: number) => {
        flexData.push({
          position: idx + 1,
          flexibility: Number(value),
        });
      });
    }

    if (flexData.length === 0) {
      return (
        <BaseResultDisplay
          title={title || '蛋白质柔性度分析'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Alert
            message="无有效数据"
            description="没有找到有效的柔性度分析结果。"
            type="warning"
            showIcon
          />
        </BaseResultDisplay>
      );
    }

    flexData.sort((a, b) => a.position - b.position);
    const chartData = flexData.map(point => [point.position, point.flexibility]);
    const currentSequence = singleEntry.sequence || result.sequence || sequence || '';

    const option = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const dataPoint = params[0];
          const idx = dataPoint.dataIndex;
          const aa = currentSequence && currentSequence.length > idx ? currentSequence.charAt(idx) : '';
          return `位置: ${dataPoint.data[0]}<br/>柔性度: ${dataPoint.data[1].toFixed(2)}<br/>氨基酸: ${aa}`;
        },
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: '残基位置',
        nameLocation: 'middle',
        nameGap: 30,
        min: Math.min(...flexData.map(point => point.position)),
        max: Math.max(...flexData.map(point => point.position)),
      },
      yAxis: {
        type: 'value',
        name: '柔性度值',
        nameLocation: 'middle',
        nameGap: 40,
      },
      series: [
        {
          type: 'line',
          data: chartData,
          smooth: true,
          lineStyle: {
            width: 2,
            color: '#722ed1',
          },
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            color: '#722ed1',
          },
        },
      ],
    };

    return (
      <BaseResultDisplay
        title={title || '蛋白质柔性度分析'}
        data={data}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
      >
        <Card
          title={`序列 ${singleEntry.sequence_id || singleEntry.id || '1'} - 柔性度分析`}
          style={{ marginBottom: 16 }}
        >
          <ReactECharts
            option={option}
            style={{ height: '400px', width: '100%' }}
            notMerge={true}
          />
          <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
            序列长度: {flexData.length} | 平均柔性度: {(flexData.reduce((sum, p) => sum + p.flexibility, 0) / flexData.length).toFixed(2)}
          </div>
        </Card>
      </BaseResultDisplay>
    );
  }

  if (Array.isArray(data) && data.length > 0) {
    return <FlexibilityResult data={{ results: data }} sequence={sequence} title={title} onDeleteResult={onDeleteResult} multiResults={multiResults} />;
  }

  return (
    <BaseResultDisplay
      title={title || '蛋白质柔性度分析'}
      data={data}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={true}
      showDistribution={false}
    >
      <Alert
        message="无有效数据"
        description="没有找到有效的柔性度分析结果。"
        type="warning"
        showIcon
      />
    </BaseResultDisplay>
  );
};

export default FlexibilityResult;